import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    type ReactNode,
} from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useUserLoggedIn } from '~/modules/user';
import { localStorage } from '~/modules/root/util/browser-storage';
import { useBoardsService } from '../hooks/use-boards-service';
import { buildSections } from '../utils/build-sections';
import type {
    FollowSelectorResult,
    FollowSelectorSection,
} from '../types/follow-selector';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import type { PinItemType } from '~/modules/moodboards/type';
import {
    addSavedPinKeys,
    fetchPinMembershipBoardIds,
    getSavedBoardIdsForPin,
    makePinKey,
    pinMembershipCacheAtom,
    savedPinKeysAtom,
} from '../state/design-board-save-state';

interface FollowSelectorContextValue {
    /** The ID of the currently open popover (null if none open) */
    openSelectorId: string | null;
    /** Open a specific follow selector */
    openFollowSelector: (
        selectorId: string,
        config: {
            selectedItemIds: number[];
            selectedItemType?: string;
        },
    ) => void;
    /** Close the currently open popover */
    closeFollowSelector: (result?: FollowSelectorResult | null) => void;
    /** Get sections for the current context */
    getSections: () => FollowSelectorSection[];
    /** Raw boards list (unfiltered by search), for deriving trigger labels. */
    boards: DesignBoardViewModel[];
    /** Whether data is loading */
    isLoading: boolean;
    /** Whether pin membership is loading */
    membershipLoading: boolean;
    /** Whether data has failed to load */
    hasError: boolean;
    /** Retry fetching data after an error */
    retry: () => void;
    /** Handle board created */
    handleBoardCreated: (board: DesignBoardViewModel) => void;
    /** Refetch data (pin membership only, boards are cached globally) */
    refetch: () => void;
    /** Refetch boards list */
    refetchBoards: () => void;
    /** Optimistically move a board to the top of the list */
    moveToTop: (boardId: number) => void;
    /** Optimistically prepend a new board to the list */
    prependBoard: (board: DesignBoardViewModel) => void;
    /** Load more paginated boards */
    loadMore: () => void;
    /** Whether more boards are available */
    hasNextPage: boolean;
    /** Whether loading more boards */
    isLoadingMore: boolean;
    /** Handle search query changes */
    handleSearch: (query: string) => void;
    /** Current search query */
    searchQuery: string;
    /** Whether search is in progress */
    isSearching: boolean;
    /** Gets saved board IDs for a specific pin from cache. */
    getSavedBoardIdsForPin: (pinId: number, pinType: PinItemType) => number[];
    /** Ensures membership for a pin is loaded into cache. */
    ensureMembershipLoaded: (
        pinId: number,
        pinType: PinItemType,
        options?: { trackLoading?: boolean },
    ) => Promise<number[]>;
}

const FollowSelectorContext = createContext<FollowSelectorContextValue | null>(
    null,
);
const LEGACY_PENDING_PINS_KEY = 'pending-mood-board-pins';

interface FollowSelectorContextProviderProps {
    children: ReactNode;
}

/**
 * Context provider for the FollowSelector.
 * Manages global state for which popover is open and provides shared data/handlers.
 */
export const FollowSelectorContextProvider = ({
    children,
}: FollowSelectorContextProviderProps) => {
    const [openSelectorId, setOpenSelectorId] = useState<string | null>(null);
    const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
    const [selectedItemType, setSelectedItemType] = useState<
        string | undefined
    >(undefined);
    const [hasError, setHasError] = useState(false);
    const [membershipLoadingKey, setMembershipLoadingKey] = useState<
        string | null
    >(null);
    const inFlightMembershipRequestsRef = useRef<
        Map<string, Promise<number[]>>
    >(new Map());
    const { isLoggedIn } = useUserLoggedIn();

    // Global boards service (singleton, fetches once)
    const boardsService = useBoardsService();
    const { initialFetch, reset: resetBoardsService } = boardsService;

    // Clear legacy pending guest saves from the retired auto-processing flow.
    useEffect(() => {
        localStorage?.removeItem(LEGACY_PENDING_PINS_KEY);
    }, []);

    const pinMembershipCache = useAtomValue(pinMembershipCacheAtom);
    const setPinMembershipCache = useSetAtom(pinMembershipCacheAtom);
    const setSavedPinKeys = useSetAtom(savedPinKeysAtom);

    const selectedPinId = selectedItemIds[0];
    const selectedPinType =
        (selectedItemType as PinItemType | undefined) ?? 'ProductPin';
    const selectedPinKey = selectedPinId
        ? makePinKey(selectedPinId, selectedPinType)
        : null;
    const membershipLoading = membershipLoadingKey === selectedPinKey;

    // Eagerly prefetch boards for logged-in users only.
    // Reset service state on logout so the next login can re-initialise.
    useEffect(() => {
        if (!isLoggedIn) {
            resetBoardsService();
            return;
        }
        initialFetch();
    }, [initialFetch, isLoggedIn, resetBoardsService]);

    const ensureMembershipLoaded = useCallback(
        async (
            pinId: number,
            pinType: PinItemType,
            options?: { trackLoading?: boolean },
        ): Promise<number[]> => {
            const trackLoading = options?.trackLoading ?? false;
            const pinKey = makePinKey(pinId, pinType);
            const cached = pinMembershipCache.get(pinKey);
            if (cached) {
                return [...cached];
            }

            const inFlightRequest =
                inFlightMembershipRequestsRef.current.get(pinKey);
            if (inFlightRequest) {
                return inFlightRequest;
            }

            if (trackLoading) {
                setMembershipLoadingKey(pinKey);
            }

            const request = fetchPinMembershipBoardIds(pinId, pinType)
                .then((boardIds) => {
                    setPinMembershipCache((prev) => {
                        const next = new Map(prev);
                        next.set(pinKey, new Set(boardIds));
                        return next;
                    });
                    if (boardIds.length > 0) {
                        setSavedPinKeys((prev) =>
                            addSavedPinKeys(prev, [pinKey]),
                        );
                    }
                    return boardIds;
                })
                .finally(() => {
                    inFlightMembershipRequestsRef.current.delete(pinKey);
                    if (trackLoading) {
                        setMembershipLoadingKey((current) =>
                            current === pinKey ? null : current,
                        );
                    }
                });

            inFlightMembershipRequestsRef.current.set(pinKey, request);
            return request;
        },
        [pinMembershipCache, setPinMembershipCache, setSavedPinKeys],
    );

    const isSavedToBoard = useCallback(
        (boardId: number): boolean => {
            if (!selectedPinId) {
                return false;
            }
            return getSavedBoardIdsForPin(
                pinMembershipCache,
                selectedPinId,
                selectedPinType,
            ).includes(boardId);
        },
        [pinMembershipCache, selectedPinId, selectedPinType],
    );

    useEffect(() => {
        if (!openSelectorId || !selectedPinId) {
            return;
        }

        if (selectedPinKey && pinMembershipCache.has(selectedPinKey)) {
            return;
        }

        setHasError(false);
        ensureMembershipLoaded(selectedPinId, selectedPinType, {
            trackLoading: true,
        }).catch((error) => {
            console.error('Error fetching pin membership:', error);
            setHasError(true);
        });
    }, [
        openSelectorId,
        selectedPinId,
        selectedPinType,
        selectedPinKey,
        pinMembershipCache,
        ensureMembershipLoaded,
    ]);

    /**
     * Open a follow selector instance.
     */
    const openFollowSelector = useCallback(
        (
            selectorId: string,
            config: {
                selectedItemIds: number[];
                selectedItemType?: string;
            },
        ) => {
            setSelectedItemIds(config.selectedItemIds);
            setSelectedItemType(config.selectedItemType);
            setOpenSelectorId(selectorId);
            setHasError(false);
        },
        [],
    );

    /**
     * Close the currently open selector.
     */
    const closeFollowSelector = useCallback(() => {
        setOpenSelectorId(null);
        setHasError(false);
        setMembershipLoadingKey(null);
    }, []);

    /**
     * Handle board created -- resets membership fetch state so the next
     * open will fetch fresh membership data.
     */
    const handleBoardCreated = useCallback(
        (_board: DesignBoardViewModel) => {
            if (!selectedPinId) {
                return;
            }
            const pinKey = makePinKey(selectedPinId, selectedPinType);
            setPinMembershipCache((prev) => {
                const next = new Map(prev);
                next.delete(pinKey);
                return next;
            });
        },
        [selectedPinId, selectedPinType, setPinMembershipCache],
    );

    const getSavedBoardIdsForPinCached = useCallback(
        (pinId: number, pinType: PinItemType): number[] => {
            return getSavedBoardIdsForPin(pinMembershipCache, pinId, pinType);
        },
        [pinMembershipCache],
    );

    const invalidateCurrentPinMembership = useCallback(() => {
        if (!selectedPinId) {
            return;
        }
        const pinKey = makePinKey(selectedPinId, selectedPinType);
        setPinMembershipCache((prev) => {
            const next = new Map(prev);
            next.delete(pinKey);
            return next;
        });
    }, [selectedPinId, selectedPinType, setPinMembershipCache]);

    const refetch = useCallback(() => {
        if (!selectedPinId) {
            return;
        }
        invalidateCurrentPinMembership();
        ensureMembershipLoaded(selectedPinId, selectedPinType, {
            trackLoading: true,
        }).catch((error) => {
            console.error('Error refetching pin membership:', error);
            setHasError(true);
        });
    }, [
        selectedPinId,
        selectedPinType,
        invalidateCurrentPinMembership,
        ensureMembershipLoaded,
    ]);

    /**
     * Retry fetching data after an error.
     */
    const retry = useCallback(() => {
        setHasError(false);
        refetch();
    }, [refetch]);

    /**
     * Get sections by combining boards + membership.
     */
    const getSections = useCallback((): FollowSelectorSection[] => {
        try {
            return buildSections(
                boardsService.boards,
                boardsService.searchResults,
                boardsService.searchQuery,
                isSavedToBoard,
            );
        } catch (error) {
            console.error('Error building sections:', error);
            setHasError(true);
            return [];
        }
    }, [
        boardsService.boards,
        boardsService.searchResults,
        boardsService.searchQuery,
        isSavedToBoard,
    ]);

    const contextValue = useMemo(
        () => ({
            openSelectorId,
            openFollowSelector,
            closeFollowSelector,
            getSections,
            boards: boardsService.boards,
            isLoading: boardsService.isLoading,
            membershipLoading,
            hasError,
            retry,
            handleBoardCreated,
            refetch,
            refetchBoards: boardsService.refetchBoards,
            moveToTop: boardsService.moveToTop,
            prependBoard: boardsService.prependBoard,
            loadMore: boardsService.loadMore,
            hasNextPage: boardsService.hasNextPage,
            isLoadingMore: boardsService.isLoadingMore,
            handleSearch: boardsService.search,
            searchQuery: boardsService.searchQuery,
            isSearching: boardsService.isSearching,
            getSavedBoardIdsForPin: getSavedBoardIdsForPinCached,
            ensureMembershipLoaded,
        }),
        [
            openSelectorId,
            openFollowSelector,
            closeFollowSelector,
            getSections,
            boardsService.boards,
            boardsService.isLoading,
            membershipLoading,
            hasError,
            retry,
            handleBoardCreated,
            refetch,
            boardsService.refetchBoards,
            boardsService.moveToTop,
            boardsService.prependBoard,
            boardsService.loadMore,
            boardsService.hasNextPage,
            boardsService.isLoadingMore,
            boardsService.search,
            boardsService.searchQuery,
            boardsService.isSearching,
            getSavedBoardIdsForPinCached,
            ensureMembershipLoaded,
        ],
    );

    return (
        <FollowSelectorContext.Provider value={contextValue}>
            {children}
        </FollowSelectorContext.Provider>
    );
};

/**
 * Hook to access the FollowSelector context.
 */
export const useFollowSelectorContext = () => {
    const context = useContext(FollowSelectorContext);
    if (!context) {
        throw new Error(
            'useFollowSelectorContext must be used within a FollowSelectorContextProvider',
        );
    }
    return context;
};

/**
 * Safe hook that returns whether any FollowSelector popover is currently open.
 * Returns false when used outside FollowSelectorContextProvider (no throw).
 */
export const useIsFollowSelectorOpen = (): boolean => {
    const context = useContext(FollowSelectorContext);
    return (
        context?.openSelectorId !== null &&
        context?.openSelectorId !== undefined
    );
};

/**
 * Hook for a specific follow selector instance to check if it should be open.
 */
export const useFollowSelectorInstance = (selectorId: string) => {
    const context = useFollowSelectorContext();
    const isOpen = context.openSelectorId === selectorId;

    return {
        isOpen,
        openFollowSelector: context.openFollowSelector,
        closeFollowSelector: context.closeFollowSelector,
        getSections: context.getSections,
        isLoading: context.isLoading,
        membershipLoading: context.membershipLoading,
        hasError: context.hasError,
        retry: context.retry,
        handleBoardCreated: context.handleBoardCreated,
        refetch: context.refetch,
        refetchBoards: context.refetchBoards,
        moveToTop: context.moveToTop,
        prependBoard: context.prependBoard,
        loadMore: context.loadMore,
        hasNextPage: context.hasNextPage,
        isLoadingMore: context.isLoadingMore,
        handleSearch: context.handleSearch,
        searchQuery: context.searchQuery,
        isSearching: context.isSearching,
    };
};
